import React, { useState, useEffect } from 'react';

interface StoreType {
  value: string;
  label: string;
}

interface StoreTypeCategory {
  category: string;
  items: StoreType[];
}

export const STORE_TYPE_CATEGORIES: StoreTypeCategory[] = [
  {
    category: 'Retail',
    items: [
      { value: 'spaeti', label: 'Spätkauf' },
      { value: 'edeka', label: 'Edeka' },
      { value: 'rewe', label: 'Rewe' },
      { value: 'kaufland', label: 'Kaufland' },
      { value: 'lidl', label: 'Lidl' },
      { value: 'aldi', label: 'Aldi' },
      { value: 'netto_marken', label: 'Netto Markendiscount' },
      { value: 'netto', label: 'Netto' },
      { value: 'penny', label: 'Penny' },
      { value: 'norma', label: 'Norma' },
      { value: 'bio_company', label: 'Bio Company' },
      { value: 'alnatura', label: 'Alnatura' },
      { value: 'denns', label: 'Denns' },
    ],
  },
  {
    category: 'HORECA',
    items: [
      { value: 'cafe', label: 'Cafe' },
      { value: 'bar', label: 'Bar' },
      { value: 'restaurant', label: 'Restaurant' },
      { value: 'hotel', label: 'Hotel' },
      { value: 'bistro', label: 'Bistro' },
      { value: 'club', label: 'Club / Nightclub' },
      { value: 'kantine', label: 'Canteen' },
      { value: 'catering', label: 'Catering' },
      { value: 'event_location', label: 'Event Location' },
      { value: 'office', label: 'Office' },
    ],
  },
  {
    category: 'Sport & Wellness',
    items: [
      { value: 'pilates', label: 'Pilates Studio' },
      { value: 'yoga', label: 'Yoga Studio' },
      { value: 'fitness', label: 'Fitness Studio' },
    ],
  },
];

export const ALL_STORE_TYPES: StoreType[] = STORE_TYPE_CATEGORIES.flatMap(
  (cat) => cat.items
);

// HORECA and Sport & Wellness are on-premise locations (not retail shelves).
const ON_PREMISE_CATEGORIES = ['HORECA', 'Sport & Wellness'];
const HORECA_VALUES = new Set(
  STORE_TYPE_CATEGORIES.filter((c) =>
    ON_PREMISE_CATEGORIES.includes(c.category)
  ).flatMap((c) => c.items.map((i) => i.value))
);

export function isHorecaType(value: string): boolean {
  return HORECA_VALUES.has(value);
}

interface StoreTypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  className?: string;
  id?: string;
  register?: any;
}

export const StoreTypeSelector: React.FC<StoreTypeSelectorProps> = ({
  value,
  onChange,
  error,
  className = '',
  id = 'store-type',
  ...props
}) => {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customType, setCustomType] = useState('');
  const [customTypes, setCustomTypes] = useState<StoreType[]>([]);

  useEffect(() => {
    if (
      value &&
      !ALL_STORE_TYPES.find((t) => t.value === value) &&
      !customTypes.find((t) => t.value === value)
    ) {
      const label = value
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      setCustomTypes((prev) => [...prev, { value, label }]);
    }
  }, [value]);

  const handleSelectChange = (selectedValue: string) => {
    if (selectedValue === '__add_new__') {
      setShowCustomInput(true);
    } else {
      onChange(selectedValue);
    }
  };

  const handleCustomSubmit = () => {
    if (customType.trim()) {
      const formattedValue = customType
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
      const formattedLabel = customType.trim();

      setCustomTypes((prev) => [
        ...prev,
        { value: formattedValue, label: formattedLabel },
      ]);
      onChange(formattedValue);
      setShowCustomInput(false);
      setCustomType('');
    }
  };

  const handleCustomCancel = () => {
    setShowCustomInput(false);
    setCustomType('');
  };

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        Store Type <span className="text-red-500">*</span>
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => handleSelectChange(e.target.value)}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm border px-3 py-2"
        {...props}
      >
        <option value="">Select store type...</option>
        {STORE_TYPE_CATEGORIES.map((category) => (
          <optgroup key={category.category} label={category.category}>
            {category.items.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        ))}
        {customTypes.length > 0 && (
          <optgroup label="Custom">
            {customTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </optgroup>
        )}
        <option value="__add_new__" className="font-semibold text-blue-600">
          + Add new store type
        </option>
      </select>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {showCustomInput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-black text-gray-900 mb-4">
              Add Custom Store Type
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Store Type Name
              </label>
              <input
                type="text"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="e.g. Biomarkt, Tankstelle"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCustomSubmit();
                  }
                }}
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCustomCancel}
                className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCustomSubmit}
                className="flex-1 px-4 py-2.5 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 transition-colors"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
